/**
 * SetupMatter Query Key Factory
 *
 * Centralized query key factory for React Query cache management.
 * Provides consistent, type-safe query keys for all setup_matter queries.
 *
 * Query keys follow a hierarchical structure for efficient cache invalidation:
 * - ['setupmatters'] - All setup_matter queries
 * - ['setupmatters', 'list'] - All list queries
 * - ['setupmatters', 'list', { filters }] - Specific filtered list
 * - ['setupmatters', 'detail', id] - Specific setup_matter by ID
 *
 * @example
 * ```typescript
 * import { setup_matterKeys } from './queryKeys';
 *
 * // Use in useQuery
 * const { data } = useQuery({
 *   queryKey: setup_matterKeys.list({ is_active: true }),
 *   queryFn: () => setup_matterService.getAll({ is_active: true }),
 * });
 *
 * // Invalidate all setup_matter queries
 * queryClient.invalidateQueries({ queryKey: setup_matterKeys.all });
 *
 * // Invalidate specific setup_matter
 * queryClient.invalidateQueries({ queryKey: setup_matterKeys.detail('uuid') });
 * ```
 *
 * Generated by CodeGen OS v1.0.0
 * Generated at: 2026-04-25T14:23:21.023262Z
 */

import type { SetupMatterFilterOptions } from '../types/setup_matter.types';

// ---- CODAIC:CUSTOM IMPORTS ----
// Add custom imports below. This section is preserved across regeneration.
// ---- CODAIC:END CUSTOM ----

/**
 * Root query key for SetupMatter entity
 * Used as namespace prefix for all setup_matter queries
 */
const SETUP_MATTER_ROOT = 'setupmatters' as const;

/**
 * SetupMatter Query Keys
 *
 * Hierarchical query key factory for setup_matter queries:
 * - all: Base key for all setup_matter queries
 * - lists: All list queries (for bulk invalidation)
 * - list(filters): Specific filtered list
 * - details: All detail queries
 * - detail(id): Specific setup_matter by ID
 * - dropdown(activeOnly): Dropdown options query
 *
 * @example
 * ```typescript
 * // List with filters
 * const key = setup_matterKeys.list({ is_active: true });
 * // Result: ['setupmatters', 'list', { is_active: true }]
 *
 * // Detail by ID
 * const key = setup_matterKeys.detail('uuid-here');
 * // Result: ['setupmatters', 'detail', 'uuid-here']
 *
 * // Invalidate all setup_matter data
 * queryClient.invalidateQueries({ queryKey: setup_matterKeys.all });
 * ```
 */
export const setup_matterKeys = {
  /**
   * Base key for all setup_matter queries
   * Use this to invalidate all setup_matter-related queries at once
   *
   * @example
   * ```typescript
   * // Invalidate everything after a major change
   * queryClient.invalidateQueries({ queryKey: setup_matterKeys.all });
   * ```
   */
  all: [SETUP_MATTER_ROOT] as const,

  /**
   * Key for all list queries
   * Use this to invalidate all list queries without affecting detail queries
   *
   * @returns Query key tuple for list namespace
   *
   * @example
   * ```typescript
   * // Invalidate all list queries
   * queryClient.invalidateQueries({ queryKey: setup_matterKeys.lists() });
   * ```
   */
  lists: () => [...setup_matterKeys.all, 'list'] as const,

  /**
   * Key for a specific filtered list query
   * Include filters in the key to enable automatic re-fetching when filters change
   *
   * @param filters - Optional filter options for the query
   * @returns Query key tuple including filters
   *
   * @example
   * ```typescript
   * // List all active
   * const key = setup_matterKeys.list({ is_active: true });
   *
   * // List with search
   * const key = setup_matterKeys.list({ search: 'test' });
   *
   * // Unfiltered list
   * const key = setup_matterKeys.list();
   * ```
   */
  list: (filters?: SetupMatterFilterOptions) =>
    [...setup_matterKeys.lists(), filters ?? {}] as const,

  /**
   * Key for all detail queries
   * Use this to invalidate all detail queries without affecting list queries
   *
   * @returns Query key tuple for detail namespace
   *
   * @example
   * ```typescript
   * // Invalidate all detail queries
   * queryClient.invalidateQueries({ queryKey: setup_matterKeys.details() });
   * ```
   */
  details: () => [...setup_matterKeys.all, 'detail'] as const,

  /**
   * Key for a specific setup_matter by ID
   * Use this for fetching and invalidating individual setup_matter records
   *
   * @param id - SetupMatter ID (UUID)
   * @returns Query key tuple for specific setup_matter
   *
   * @example
   * ```typescript
   * // Fetch specific setup_matter
   * const { data } = useQuery({
   *   queryKey: setup_matterKeys.detail('uuid-here'),
   *   queryFn: () => setup_matterService.getById('uuid-here'),
   * });
   *
   * // Invalidate after update
   * queryClient.invalidateQueries({
   *   queryKey: setup_matterKeys.detail('uuid-here')
   * });
   * ```
   */
  detail: (id: string) => [...setup_matterKeys.details(), id] as const,

  /**
   * Key for dropdown options query
   * Separate key allows independent caching of dropdown data
   *
   * @param activeOnly - Whether to include only active setup_matters (default: true)
   * @returns Query key tuple for dropdown query
   *
   * @example
   * ```typescript
   * // Active items only (default)
   * const key = setup_matterKeys.dropdown();
   *
   * // Include inactive items
   * const key = setup_matterKeys.dropdown(false);
   * ```
   */
  dropdown: (activeOnly: boolean = true) =>
    [...setup_matterKeys.all, 'dropdown', { activeOnly }] as const,

  /**
   * SetupMatters filtered by client company ID
   *
   * @param clientCompanyId - ClientCompany ID (UUID)
   * @returns Query key tuple
   */
  byClientCompany: (clientCompanyId: string) =>
    [...setup_matterKeys.all, 'by-client-company', clientCompanyId] as const,

  /**
   * SetupMatters filtered by transaction type ID
   *
   * @param transactionTypeId - TransactionType ID (UUID)
   * @returns Query key tuple
   */
  byTransactionType: (transactionTypeId: string) =>
    [...setup_matterKeys.all, 'by-transaction-type', transactionTypeId] as const,

  /**
   * SetupMatters filtered by billing entity ID
   *
   * @param billingEntityId - BillingEntity ID (UUID)
   * @returns Query key tuple
   */
  byBillingEntity: (billingEntityId: string) =>
    [...setup_matterKeys.all, 'by-billing-entity', billingEntityId] as const,

  /**
   * SetupMatters filtered by lead lawyer ID
   *
   * @param leadLawyerId - LeadLawyer ID (UUID)
   * @returns Query key tuple
   */
  byLeadLawyer: (leadLawyerId: string) =>
    [...setup_matterKeys.all, 'by-lead-lawyer', leadLawyerId] as const,

  /**
   * SetupMatters filtered by status
   *
   * @param status - Status value
   * @returns Query key tuple
   */
  byStatus: (status: string) =>
    [...setup_matterKeys.all, 'by-status', status] as const,
} as const;

/**
 * Type representing any valid SetupMatter query key
 * Useful for typing query key arrays in generic contexts
 */
export type SetupMatterQueryKey =
  | typeof setup_matterKeys.all
  | ReturnType<typeof setup_matterKeys.lists>
  | ReturnType<typeof setup_matterKeys.list>
  | ReturnType<typeof setup_matterKeys.details>
  | ReturnType<typeof setup_matterKeys.detail>
  | ReturnType<typeof setup_matterKeys.dropdown>
;

// ---- CODAIC:CUSTOM HOOKS ----
// Add custom query key factories below. This section is preserved across regeneration.
// ---- CODAIC:END CUSTOM ----

/**
 * Default export for convenience
 */
export default setup_matterKeys;
